import { useState, useEffect, useRef } from 'react';
import { Sparkles, Send, RefreshCw, Lock, ArrowUpRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MONO = { fontFamily: "'IBM Plex Mono', monospace" };
const SERIF = { fontFamily: "'Bodoni Moda', Georgia, serif" };

const SUGGESTED = [
  'Best-fit role for this profile?',
  'Key strengths & any red flags?',
  'How senior is their experience really?',
  'Would they suit a luxury house?',
];

// ── RECON locked teaser ───────────────────────────────────────────────────────
function LockedBrief({ navigate }) {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-0">
      <div className="border border-t-0 border-border" data-testid="ai-brief-locked">
        <div className="border-b border-border px-4 sm:px-8 py-4 sm:py-5 flex items-center justify-between bg-[hsl(0,0%,98%)]">
          <div className="flex items-center gap-3">
            <Sparkles className="w-4 h-4 text-[hsl(var(--primary))]" strokeWidth={1.5} />
            <span style={{ ...MONO, fontSize: '10px', letterSpacing: '0.18em' }}
              className="uppercase text-foreground font-medium">AI Analyst — Profile Snapshot</span>
          </div>
          <span style={{ ...MONO, fontSize: '9px', letterSpacing: '0.1em' }}
            className="text-muted-foreground/50 uppercase border border-border px-2 py-0.5">INTELLIGENCE</span>
        </div>
        <div className="px-4 sm:px-8 py-7 relative">
          <div className="space-y-2 opacity-25 select-none pointer-events-none blur-[1.5px]">
            <div className="h-3 bg-foreground/30 w-[92%]" />
            <div className="h-3 bg-foreground/30 w-[85%]" />
            <div className="h-3 bg-foreground/30 w-[60%]" />
          </div>
          <div className="mt-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <Lock className="w-4 h-4 text-[hsl(var(--primary))] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
              <div>
                <p className="text-sm font-semibold text-foreground mb-1">Ask AI anything about this candidate</p>
                <p className="text-sm text-muted-foreground max-w-lg">
                  An instant, recruiter-grade snapshot plus a private AI analyst that answers your questions
                  on fit, strengths and risk — grounded in every signal we hold. Included with INTELLIGENCE.
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/pricing')}
              className="rounded-none bg-foreground text-background hover:bg-foreground/80 transition-colors px-6 py-2.5 text-sm flex-shrink-0"
              data-testid="ai-brief-upgrade-btn"
            >
              Unlock Intelligence
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main panel (INTELLIGENCE) ─────────────────────────────────────────────────
export default function AIBrief({ candidateId, candidateName, isIntelligence }) {
  const navigate = useNavigate();
  const [brief, setBrief] = useState('');
  const [loadingBrief, setLoadingBrief] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [messages, setMessages] = useState([]); // {role:'user'|'assistant', content}
  const [input, setInput] = useState('');
  const [asking, setAsking] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!isIntelligence) { setLoadingBrief(false); return; }
    let active = true;
    setLoadingBrief(true);
    authFetch(`${BACKEND_URL}/api/candidates/${candidateId}/ai-brief`)
      .then(r => r.json())
      .then(d => { if (active && d.success) setBrief(d.brief || ''); })
      .catch(() => {})
      .finally(() => active && setLoadingBrief(false));
    return () => { active = false; };
  }, [candidateId, isIntelligence]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, asking]);

  const refreshBrief = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/candidates/${candidateId}/ai-brief?refresh=true`);
      const d = await r.json();
      if (d.success) setBrief(d.brief || '');
    } catch { /* noop */ }
    setRefreshing(false);
  };

  const ask = async (q) => {
    const question = (q ?? input).trim();
    if (!question || asking) return;
    const history = messages.map(m => ({ role: m.role, content: m.content }));
    setMessages(prev => [...prev, { role: 'user', content: question }]);
    setInput('');
    setAsking(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/candidates/${candidateId}/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question, history }),
      });
      const d = await r.json();
      setMessages(prev => [...prev, { role: 'assistant', content: d.success ? d.answer : 'The assistant is unavailable right now. Please try again.' }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Connection error. Please try again.' }]);
    }
    setAsking(false);
  };

  if (!isIntelligence) return <LockedBrief navigate={navigate} />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-0">
      <div className="border border-t-0 border-border bg-gradient-to-b from-[hsl(0,0%,99%)] to-white" data-testid="ai-brief-panel">
        {/* Header */}
        <div className="border-b border-border px-4 sm:px-8 py-4 sm:py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="w-4 h-4 text-[hsl(var(--primary))]" strokeWidth={1.5} />
            <span style={{ ...MONO, fontSize: '10px', letterSpacing: '0.18em' }}
              className="uppercase text-foreground font-medium">AI Analyst — Profile Snapshot</span>
          </div>
          <button
            onClick={refreshBrief}
            disabled={refreshing || loadingBrief}
            className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
            data-testid="ai-brief-refresh"
          >
            <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} strokeWidth={1.5} />
            <span style={{ ...MONO, fontSize: '9px', letterSpacing: '0.1em' }} className="uppercase hidden sm:inline">
              {refreshing ? 'Regenerating' : 'Regenerate'}
            </span>
          </button>
        </div>

        {/* Snapshot body */}
        <div className="px-4 sm:px-8 py-6 sm:py-8 border-b border-border">
          {loadingBrief ? (
            <div className="space-y-2.5 animate-pulse" data-testid="ai-brief-loading">
              <div className="h-3.5 bg-foreground/10 w-[94%]" />
              <div className="h-3.5 bg-foreground/10 w-[88%]" />
              <div className="h-3.5 bg-foreground/10 w-[55%]" />
              <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.12em' }} className="text-muted-foreground/60 uppercase pt-2">
                Synthesising intelligence snapshot…
              </p>
            </div>
          ) : (
            <p style={{ ...SERIF }} className="text-foreground leading-relaxed text-lg sm:text-xl"
              data-testid="ai-brief-text">
              {brief || 'No snapshot available for this profile yet.'}
            </p>
          )}
        </div>

        {/* Ask the Analyst */}
        <div className="px-4 sm:px-8 py-5 sm:py-6">
          <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.15em' }}
            className="text-muted-foreground uppercase mb-3">Ask the Analyst</p>

          {/* Conversation */}
          {messages.length > 0 && (
            <div ref={scrollRef} className="space-y-4 mb-4 max-h-72 overflow-y-auto pr-1" data-testid="ai-ask-thread">
              {messages.map((m, i) => (
                m.role === 'user' ? (
                  <div key={i} className="flex justify-end">
                    <div className="bg-foreground text-background px-3.5 py-2 max-w-[85%] text-sm" data-testid="ai-ask-question">
                      {m.content}
                    </div>
                  </div>
                ) : (
                  <div key={i} className="flex items-start gap-2.5 max-w-[90%]" data-testid="ai-ask-answer">
                    <Sparkles className="w-3.5 h-3.5 text-[hsl(var(--primary))] mt-1 flex-shrink-0" strokeWidth={1.5} />
                    <p className="text-sm text-foreground leading-relaxed">{m.content}</p>
                  </div>
                )
              ))}
              {asking && (
                <div className="flex items-center gap-2.5" data-testid="ai-ask-thinking">
                  <Sparkles className="w-3.5 h-3.5 text-[hsl(var(--primary))] flex-shrink-0" strokeWidth={1.5} />
                  <span className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 bg-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 bg-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Suggested prompts — stay available throughout the conversation */}
          {!loadingBrief && !asking && (
            <div className="flex flex-wrap gap-2 mb-3">
              {SUGGESTED.map((s, i) => (
                <button
                  key={i}
                  onClick={() => ask(s)}
                  style={{ ...MONO, fontSize: '10px', letterSpacing: '0.04em' }}
                  className="border border-border px-3 py-1.5 text-muted-foreground hover:border-foreground hover:text-foreground transition-colors"
                  data-testid={`ai-ask-suggestion-${i}`}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="flex items-center gap-2 border border-border focus-within:border-foreground transition-colors bg-white">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') ask(); }}
              placeholder={`Ask anything about ${candidateName?.split(' ')[0] || 'this candidate'}…`}
              className="flex-1 px-3.5 py-3 text-sm bg-transparent outline-none placeholder:text-muted-foreground/60"
              data-testid="ai-ask-input"
            />
            <button
              onClick={() => ask()}
              disabled={asking || !input.trim()}
              className="px-4 py-3 text-foreground hover:bg-secondary transition-colors disabled:opacity-30"
              data-testid="ai-ask-send"
            >
              <Send className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
          <p style={{ ...MONO, fontSize: '8px', letterSpacing: '0.08em' }}
            className="text-muted-foreground/50 uppercase mt-2 flex items-center gap-1">
            <ArrowUpRight className="w-2.5 h-2.5" strokeWidth={1.5} />
            Answers are AI-generated from FashionSpyder signals · contact details stay protected
          </p>
          <p style={{ ...MONO, fontSize: '8px', letterSpacing: '0.06em' }}
            className="text-muted-foreground/50 mt-1.5 leading-relaxed normal-case">
            StyleDNA and AI insights use FashionSpyder's proprietary algorithm. AI can make mistakes — use these
            insights to support, not replace, your team's judgement and human review.
          </p>
        </div>
      </div>
    </div>
  );
}
